import { Root } from '@teams/Root';
import { publicRoutes, tabRoutes, teamsPaths } from '@teams/routes/AppRoutes';
import { useAuthStore } from '@terminal/stores/use-auth/store/authStore';
import React, { Component, ReactNode } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  RouterProvider,
} from 'react-router';

// Lightweight error boundary that doesn't depend on Grafana Faro
class AppErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.error('[TeamsTab] Unhandled render error:', error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem', color: 'var(--color-text-default)' }}>
          Something went wrong. Please reload the tab.
        </div>
      );
    }

    return this.props.children;
  }
}

const ProtectedRoute = ({ element }: { element: React.ReactElement }) => {
  const isAuthenticated = useAuthStore(state => !!state.token);

  if (!isAuthenticated) {
    return null;
  }

  return element;
};

export const Routes = () => {
  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route
        path="/app"
        element={
          <AppErrorBoundary>
            <Root />
          </AppErrorBoundary>
        }
      >
        <Route index element={<Navigate to={teamsPaths.alfred} replace />} />

        {tabRoutes.map(route => (
          <Route
            key={route.path}
            path={route.path}
            element={<ProtectedRoute element={route.element} />}
          />
        ))}

        {publicRoutes.map(route => (
          <Route key={route.path} path={route.path} element={route.element} />
        ))}

        <Route path="*" element={<Navigate to={teamsPaths.alfred} replace />} />
      </Route>
    )
  );

  return <RouterProvider router={router} />;
};
